/**
 * DeptMembersModal — admin-only membership manager for one department.
 *
 * Membership lives on authorized_users/{uid}.portals (same field the Team
 * Portals directory and Manage Team use), so a member added here shows up in
 * those views too. New people are pre-approved via access_requests and
 * auto-activated by the autoActivatePreApproved function when they register.
 *
 * Props: dept ({ id, name, color }), userName, onClose
 */
import { useState, useEffect, useMemo } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, onSnapshot, doc, updateDoc, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { portalsOf } from '../../utils/viewAs';

let _db = null;
const db = () => { if (!_db) { try { _db = getFirestore(getApp()); } catch (_) {} } return _db; };

const C = {
  bg:     '#0c112e',
  panel:  '#141936',
  border: '#2e3351',
  text:   '#dee0ff',
  bright: '#ffffff',
  dim:    '#64748b',
  error:  '#f43f5e',
  ok:     '#10b981',
  mono:   'monospace',
  sans:   "'Inter', system-ui, sans-serif",
};

const inputStyle = {
  width: '100%', background: C.bg, border: `1px solid ${C.border}`, color: C.text,
  padding: '8px 11px', fontFamily: C.mono, fontSize: 11, outline: 'none', boxSizing: 'border-box',
};

const label = text => (
  <div style={{ fontFamily: C.mono, fontSize: 9, color: C.dim, letterSpacing: 2, marginBottom: 8 }}>{text}</div>
);

export default function DeptMembersModal({ dept, userName, onClose }) {
  const accent = dept.color || '#6366f1';
  const [users,  setUsers]  = useState([]);
  const [search, setSearch] = useState('');
  const [busy,   setBusy]   = useState({});
  const [name,   setName]   = useState('');
  const [email,  setEmail]  = useState('');
  const [inviting, setInviting] = useState(false);
  const [msg, setMsg] = useState(null); // { text, ok }

  useEffect(() => onSnapshot(
    collection(db(), 'authorized_users'),
    snap => setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() }))),
    () => setMsg({ text: 'Could not load team members', ok: false }),
  ), []);

  const active = users.filter(u => u.active !== false && u.status !== 'revoked');
  const members = active.filter(u => portalsOf(u).includes(dept.id));
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return active
      .filter(u => !portalsOf(u).includes(dept.id))
      .filter(u => !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
      .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
  }, [active, search, dept.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function setPortals(u, next, doneText) {
    setBusy(b => ({ ...b, [u.uid]: true }));
    try {
      await updateDoc(doc(db(), 'authorized_users', u.uid), {
        portals: next,
        portalsUpdatedAt: serverTimestamp(),
      });
      setMsg({ text: doneText, ok: true });
    } catch (e) {
      setMsg({ text: `Failed: ${e.message}`, ok: false });
    }
    setBusy(b => ({ ...b, [u.uid]: false }));
  }

  function addMember(u) {
    setPortals(u, [...portalsOf(u), dept.id], `✓ ${u.name || u.email} added to ${dept.name}`);
  }

  function removeMember(u) {
    const next = portalsOf(u).filter(p => p !== dept.id);
    if (!next.length) {
      setMsg({ text: `${u.name || u.email} has no other portal — assign one in Manage Team first`, ok: false });
      return;
    }
    setPortals(u, next, `${u.name || u.email} removed from ${dept.name}`);
  }

  async function invite(e) {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!name.trim() || !em.includes('@')) { setMsg({ text: 'Name and a valid email are required', ok: false }); return; }
    const existing = users.find(u => u.email?.toLowerCase() === em);
    if (existing) {
      if (portalsOf(existing).includes(dept.id)) setMsg({ text: `${em} is already a member`, ok: false });
      else addMember(existing);
      return;
    }
    setInviting(true);
    try {
      await addDoc(collection(db(), 'access_requests'), {
        uid: null,
        name: name.trim(),
        email: email.trim(),
        team: dept.id,
        reason: `Added to ${dept.name} by admin`,
        status: 'pre_approved',
        role: dept.id,
        portals: [dept.id],
        preApprovedEmail: em,
        requestedAt: serverTimestamp(),
        approvedAt: serverTimestamp(),
        approvedBy: userName || 'Admin',
      });
      setMsg({ text: `✓ ${name.trim()} pre-approved — auto-activated in ${dept.name} when they register with ${em}`, ok: true });
      setName(''); setEmail('');
    } catch (ex) {
      setMsg({ text: `Failed: ${ex.message}`, ok: false });
    }
    setInviting(false);
  }

  const btn = (color, filled) => ({
    padding: '5px 12px', fontFamily: C.mono, fontSize: 9, letterSpacing: 1, cursor: 'pointer', flexShrink: 0,
    background: filled ? `${color}22` : 'transparent', border: `1px solid ${color}`, color,
  });

  const row = (u, action) => (
    <div key={u.uid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
      borderBottom: `1px solid ${C.border}55` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: C.sans, fontSize: 12, fontWeight: 600, color: C.bright,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || u.email}</div>
        <div style={{ fontFamily: C.mono, fontSize: 9, color: C.dim,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {u.email}{u.role ? ` · ${u.role}` : ''}
        </div>
      </div>
      {busy[u.uid] ? <span style={{ fontFamily: C.mono, fontSize: 9, color: C.dim }}>SAVING…</span> : action}
    </div>
  );

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 700,
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 16px' }}>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, width: '100%', maxWidth: 560 }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: C.dim, letterSpacing: 2 }}>TEAM MEMBERS</div>
            <div style={{ fontFamily: C.sans, fontSize: 15, fontWeight: 700, color: accent }}>{dept.name}</div>
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {msg && (
            <div style={{ fontFamily: C.mono, fontSize: 10, lineHeight: 1.6, padding: '8px 12px',
              color: msg.ok ? C.ok : C.error, border: `1px solid ${msg.ok ? C.ok : C.error}55`,
              background: `${msg.ok ? C.ok : C.error}12` }}>{msg.text}</div>
          )}

          <div>
            {label(`CURRENT MEMBERS (${members.length})`)}
            {members.length === 0
              ? <div style={{ fontFamily: C.mono, fontSize: 10, color: C.dim, padding: '6px 0' }}>No members yet.</div>
              : members.map(u => row(u, <button onClick={() => removeMember(u)} style={btn(C.error)}>REMOVE</button>))}
          </div>

          <div>
            {label('ADD EXISTING TEAM MEMBER')}
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email…" style={{ ...inputStyle, marginBottom: 6 }} />
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {candidates.length === 0
                ? <div style={{ fontFamily: C.mono, fontSize: 10, color: C.dim, padding: '6px 0' }}>
                    {search ? 'No matching team members.' : 'Everyone is already a member.'}
                  </div>
                : candidates.map(u => row(u, <button onClick={() => addMember(u)} style={btn(accent, true)}>+ ADD</button>))}
            </div>
          </div>

          <form onSubmit={invite}>
            {label('INVITE NEW PERSON')}
            <div style={{ fontFamily: C.mono, fontSize: 9, color: C.dim, lineHeight: 1.6, marginBottom: 8 }}>
              Pre-approves them — their account is activated in this department when they register with this email.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name"
                style={{ ...inputStyle, flex: '1 1 160px', width: 'auto' }} />
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="user@example.com"
                style={{ ...inputStyle, flex: '1 1 200px', width: 'auto' }} />
              <button type="submit" disabled={inviting} style={{ ...btn(accent, true), padding: '8px 16px' }}>
                {inviting ? 'ADDING…' : 'INVITE →'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
